import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, UserCheck, UserPlus, UserX, Search } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { matchUsers, parseFullName } from '../utils/matchUsers';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

export function UsersReviewStep({ parsedUsers, userMatches, setUserMatches }) {
  const { company } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  // Fetch company members and run matching on mount
  useEffect(() => {
    if (parsedUsers.length > 0 && userMatches.length === 0) {
      runMatching();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedUsers]);

  const runMatching = useCallback(async () => {
    if (!company?.id || parsedUsers.length === 0) return;
    setLoading(true);

    try {
      const { data: members } = await supabase
        .from('user_profiles')
        .select('id, email, first_name, last_name, avatar_url')
        .eq('company_id', company.id);

      const results = matchUsers(parsedUsers, members || []);

      // For create_new users, pre-fill first/last name
      const enriched = results.map(r => {
        if (r.action === 'create_new' || r.action === 'skip') {
          const { firstName, lastName } = parseFullName(r.rawName);
          return { ...r, firstName, lastName };
        }
        return r;
      });

      setUserMatches(enriched);
    } catch (err) {
      console.error('Error matching users:', err);
    } finally {
      setLoading(false);
    }
  }, [company?.id, parsedUsers, setUserMatches]);

  const updateMatch = useCallback((index, updates) => {
    setUserMatches(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  }, [setUserMatches]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">Matching users against company members...</p>
      </div>
    );
  }

  const matched = userMatches.filter(u => u.action === 'match');
  const createNew = userMatches.filter(u => u.action === 'create_new');
  const skipped = userMatches.filter(u => u.action === 'skip');

  const filtered = searchFilter
    ? userMatches.filter(u => u.rawName.toLowerCase().includes(searchFilter.toLowerCase()))
    : userMatches;

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <Badge variant="outline" className="gap-1.5 py-1">
          <UserCheck className="h-3.5 w-3.5 text-green-600" />
          {matched.length} matched
        </Badge>
        <Badge variant="outline" className="gap-1.5 py-1">
          <UserPlus className="h-3.5 w-3.5 text-blue-600" />
          {createNew.length} new
        </Badge>
        <Badge variant="outline" className="gap-1.5 py-1">
          <UserX className="h-3.5 w-3.5 text-gray-400" />
          {skipped.length} skipped
        </Badge>

        <div className="ml-auto relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input
            placeholder="Filter users..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="h-8 pl-8 w-56 text-sm"
          />
        </div>
      </div>

      {/* Users table */}
      <ScrollArea className="h-[420px] border rounded-lg">
        <div className="divide-y">
          {filtered.map((user) => {
            const realIndex = userMatches.indexOf(user);
            return (
              <div key={realIndex} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50">
                {/* Raw name */}
                <div className="w-48 flex-shrink-0">
                  <p className="text-sm font-medium truncate">{user.rawName}</p>
                  {user.email && (
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  )}
                </div>

                {/* Match indicator */}
                <div className="w-28 flex-shrink-0">
                  {user.matchType === 'email_exact' && (
                    <Badge className="bg-green-100 text-green-800 text-xs">Email match</Badge>
                  )}
                  {user.matchType === 'name_exact' && (
                    <Badge className="bg-green-100 text-green-800 text-xs">Name match</Badge>
                  )}
                  {user.matchType === 'name_fuzzy' && (
                    <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                      Fuzzy ({Math.round(user.confidence * 100)}%)
                    </Badge>
                  )}
                  {user.matchType === 'none' && (
                    <Badge variant="secondary" className="text-xs">No match</Badge>
                  )}
                </div>

                {/* Matched profile info or new user fields */}
                <div className="flex-1 min-w-0">
                  {user.action === 'match' && user.matchedProfile && (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={user.matchedProfile.avatar_url} />
                        <AvatarFallback className="text-[10px]">
                          {(user.matchedProfile.first_name?.[0] || '')}{(user.matchedProfile.last_name?.[0] || '')}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm truncate">
                        {user.matchedProfile.first_name} {user.matchedProfile.last_name}
                      </span>
                      <span className="text-xs text-muted-foreground truncate">
                        {user.matchedProfile.email}
                      </span>
                    </div>
                  )}
                  {user.action === 'create_new' && (
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="First name"
                        value={user.firstName || ''}
                        onChange={(e) => updateMatch(realIndex, { firstName: e.target.value })}
                        className="h-7 text-xs w-28"
                      />
                      <Input
                        placeholder="Last name"
                        value={user.lastName || ''}
                        onChange={(e) => updateMatch(realIndex, { lastName: e.target.value })}
                        className="h-7 text-xs w-28"
                      />
                    </div>
                  )}
                  {user.action === 'skip' && (
                    <span className="text-xs text-muted-foreground italic">Will be skipped</span>
                  )}
                </div>

                {/* Action selector */}
                <Select
                  value={user.action}
                  onValueChange={(val) => updateMatch(realIndex, { action: val })}
                >
                  <SelectTrigger className="w-32 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {user.matchedProfile && (
                      <SelectItem value="match">Match</SelectItem>
                    )}
                    <SelectItem value="create_new">Create New</SelectItem>
                    <SelectItem value="skip">Skip</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-8 text-sm text-muted-foreground">
              {searchFilter ? 'No users match your filter' : 'No users found in the spreadsheet'}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
